import { useEffect, useRef, useState } from 'react';
import { useClampedMenuPosition } from './useClampedMenuPosition.ts';

export interface ContextMenuPosition {
  x: number;
  y: number;
  sha: string;
}

function SparkleIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1.2 L9.4 6.4 L14.6 8 L9.4 9.6 L8 14.8 L6.6 9.6 L1.4 8 L6.6 6.4 Z" />
    </svg>
  );
}

function CherryPickIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="12" r="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="4" r="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.6 10.6 Q8.5 7.5 10.4 5.6" stroke="currentColor" strokeWidth="1.4" strokeDasharray="1.6 1.6" strokeLinecap="round" />
      <path d="M10.4 5.6 L8.6 5.9 M10.4 5.6 L10.1 7.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function CreateBranchIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="3" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="4" cy="13" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="8" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4 4.6 V 11.4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4 8.6 C 6 8.6 8 8 10.4 8" stroke="currentColor" strokeWidth="1.4" fill="none" />
    </svg>
  );
}

// Même forme d'étiquette que PricingIcon (SettingsModal.tsx) — un tag reste un tag visuellement,
// épaisseur de trait alignée sur les autres icônes de ce menu (1.4 plutôt que 1.3).
function CreateTagIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.6 2H13a1 1 0 0 1 1 1v4.4a1 1 0 0 1-.3.7l-5.6 5.6a1 1 0 0 1-1.4 0L2.3 9.3a1 1 0 0 1 0-1.4L7.9 2.3a1 1 0 0 1 .7-.3Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />
      <circle cx="10.5" cy="5.5" r="1" fill="currentColor" />
    </svg>
  );
}

function RangeStartIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 2v12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M4 2.8h6.5l-1.7 2.3 1.7 2.3H4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function RangeEndIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 2v12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M4 2.8h6.5l-1.7 2.3 1.7 2.3H4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="rgba(255,255,255,0.15)" />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Même paire d'icônes que CredentialField (SettingsModal.tsx) — copier/copié, réutilisée ici pour
// le SHA complet du commit plutôt qu'un secret.
function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <rect x="5" y="5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.5 10.5V3.5a1 1 0 0 1 1-1H10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function CopiedIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <path d="M3.5 8.4 L6.5 11.4 L12.5 4.6" stroke="var(--success)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface Props {
  position: ContextMenuPosition;
  onClose: () => void;
  onSummarize: () => void;
  onStartCherryPick: () => void;
  onStartCreateBranch: () => void;
  onStartCreateTag: () => void;
  onMarkRangeStart: () => void;
  onCancelRangeStart: () => void;
  onMarkRangeEnd: () => void;
  rangeStartSha: string | null;
  canMarkRangeEnd: boolean;
  canCreateBranch: boolean;
  canCreateTag: boolean;
  aiEnabled: boolean;
}

export function CommitContextMenu({
  position,
  onClose,
  onSummarize,
  onStartCherryPick,
  onStartCreateBranch,
  onStartCreateTag,
  onMarkRangeStart,
  onMarkRangeEnd,
  rangeStartSha,
  canMarkRangeEnd,
  canCreateBranch,
  canCreateTag,
  onCancelRangeStart,
  aiEnabled,
}: Props) {
  const isRangeStart = position.sha === rangeStartSha;
  const ref = useRef<HTMLDivElement>(null);
  const { left, top } = useClampedMenuPosition(ref, position.x, position.y);

  // Accusé-réception transitoire, même pattern que CredentialField (SettingsModal.tsx) —
  // setTimeout 1500ms plutôt qu'un état qui reste jusqu'au prochain clic.
  const [copied, setCopied] = useState(false);
  const handleCopySha = () => {
    void navigator.clipboard.writeText(position.sha);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="commit-context-menu"
      style={{ left, top }}
    >
      <div className="ccm-header">
        <span className="ccm-sha">{position.sha.slice(0, 8)}</span>
        <button
          type="button"
          className="ccm-copy-btn"
          title={copied ? 'Copié !' : 'Copier le SHA complet'}
          onClick={handleCopySha}
        >
          {copied ? <CopiedIcon /> : <CopyIcon />}
        </button>
      </div>
      <div className="ccm-separator" />
      <button className="ccm-item" onClick={onSummarize} disabled={!aiEnabled} title={aiEnabled ? undefined : 'Clé API Claude non configurée (Paramètres → Authentification)'}>
        <SparkleIcon />
        <span>Résumer ce commit</span>
        <span className="ccm-tag">IA</span>
      </button>
      <button className="ccm-item" onClick={onStartCherryPick}>
        <CherryPickIcon />
        <span>Source cherry-pick…</span>
      </button>
      <button
        className="ccm-item"
        onClick={onStartCreateBranch}
        disabled={!canCreateBranch}
        title={canCreateBranch ? undefined : 'Ton rôle sur ce dépôt (Guest/Reporter) ne permet pas de créer une branche'}
      >
        <CreateBranchIcon />
        <span>Créer une branche…</span>
      </button>
      <button
        className="ccm-item"
        onClick={onStartCreateTag}
        disabled={!canCreateTag}
        title={canCreateTag ? undefined : 'Ton rôle sur ce dépôt (Guest/Reporter) ne permet pas de créer un tag'}
      >
        <CreateTagIcon />
        <span>Créer un tag…</span>
      </button>
      <div className="ccm-separator" />
      {isRangeStart ? (
        <button className="ccm-item" onClick={onCancelRangeStart}>
          <CancelIcon />
          <span>Annuler le début de plage</span>
        </button>
      ) : (
        <button className="ccm-item" onClick={onMarkRangeStart}>
          <RangeStartIcon />
          <span>Début de plage</span>
        </button>
      )}
      <button className="ccm-item" onClick={onMarkRangeEnd} disabled={!canMarkRangeEnd || !aiEnabled} title={aiEnabled ? undefined : 'Clé API Claude non configurée (Paramètres → Authentification)'}>
        <RangeEndIcon />
        <span>Fin de plage (résumer)</span>
      </button>
    </div>
  );
}
